import {
  Button,
  Cell,
  Chip,
  EmbeddedButton,
  LoadingSpinner,
  styled,
  Tooltip,
} from '@zeiss/beyond-online-react';
import {
  Box,
  ButtonGroup,
  Grid,
  PageHeader,
  ReadOnlyField,
  Section,
  SectionHeadline,
  SectionText,
} from '@zeiss/lib-components';
import { format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { InvitationActions, useInvitationAuthorization } from './';
import {
  DEFAULT_DATE_TIME_FORMAT,
  DEFAULT_LOCALE,
  INVITATION_EXPIRATION,
  NOT_AVAILABLE,
} from 'src/constants';
import { useInvitation } from 'src/hooks/api';
import { SendInvitationModal } from 'src/modules/Invitations/components/SendInvitationModal';
import { useInvitationClipboard } from 'src/modules/Invitations/hooks';
import {
  getStatusChip,
  isInvitationActive,
} from 'src/modules/Invitations/utils';
import { StandardRouteParams } from 'src/types';

const BarContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  flex-direction: column;
  gap: var(--spacing-m);
  margin-bottom: var(--spacing-responsive-m);

  @media (width >= 1024px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const ChipContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-flow: row wrap;
  gap: var(--spacing-xs);
`;

const IconButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

/**
 * TODO:
 * - PageHeader needs refactoring. Spacing should come from the container, not
 *   the headline. Some screen positions like "Invitation" require different
 *   spacing due visual balance. Current spacing delta to Figma is accepted.
 * - Add resend cooldown to limit how fast you can resend.
 */
export const Invitation = () => {
  const { t } = useTranslation(['common', 'invitations']);
  const { code: routeCode } = useParams<StandardRouteParams>();
  const [searchParams] = useSearchParams();
  const { copyInvitationLink, textCopied } = useInvitationClipboard();

  const applicationId = searchParams.get('applicationId') ?? undefined;
  const tenantId = searchParams.get('tenantId') ?? undefined;

  const {
    can,
    isError: isErrorEvaluations,
    isPending: isPendingEvaluations,
  } = useInvitationAuthorization({ applicationId, tenantId });

  const [isSendInvitationModalOpen, setSendInvitationModalOpen] =
    useState(false);

  const { data, error, isPending } = useInvitation(routeCode, {
    // Only fetch once the user is confirmed authorized
    enabled: !isPendingEvaluations && !isErrorEvaluations,
  });

  if (isPending || isPendingEvaluations) {
    return (
      <LoadingSpinner label={t('common:loadingFeedback.loading')} size="xl" />
    );
  }

  if (!data || error || isErrorEvaluations) {
    return <div>Error loading data</div>;
  }

  const {
    additionalData,
    applicationName,
    code,
    createdAt,
    email,
    firstName,
    invitationUrl,
    lastName,
    locale,
    location,
    redirectUrl,
    sentCount,
    status,
    statusLastModifiedAt,
    tenantName,
    type,
  } = data;

  const createdAtDate = format(new Date(createdAt), DEFAULT_DATE_TIME_FORMAT);
  const lastModifiedDate = format(
    new Date(statusLastModifiedAt),
    DEFAULT_DATE_TIME_FORMAT,
  );

  const expiryDate = new Date(
    new Date(createdAt).getTime() + INVITATION_EXPIRATION,
  );

  const invitedToName = type === 'application' ? applicationName : tenantName;

  const languageNames = new Intl.DisplayNames(['en'], {
    type: 'language',
  });
  const displayedLanguage = languageNames.of(locale ?? DEFAULT_LOCALE);

  const invitationSentCount = sentCount ?? 0;

  const showSendInvitationModal = () => {
    setSendInvitationModalOpen(true);
  };

  const closeSendInvitationModal = () => {
    setSendInvitationModalOpen(false);
  };

  // Sending and copying is sharing the same evaluation wrapped container
  const canSend = can(InvitationActions.SEND) && isInvitationActive(status);

  return (
    <>
      <PageHeader eyebrow={t('invitations:inviteCode')} headline={code} />

      <BarContainer>
        <ChipContainer>
          {getStatusChip({
            expiry: { date: expiryDate, show: true },
            status,
            t,
          })}
          <Chip
            color="info"
            label={t('common:lastUpdatedOnDate', { date: lastModifiedDate })}
          />
          <Chip
            color="gray"
            label={t('common:createdOnDate', { date: createdAtDate })}
          />
        </ChipContainer>

        {canSend ? (
          <ButtonGroup spacing="m">
            <IconButtonContainer>
              <Tooltip
                anchorEl={
                  <EmbeddedButton
                    aria-label={t('invitations:action.copyInvitationLink')}
                    icon={textCopied ? 'Checkmark' : 'CopyContent'}
                    onClick={() => copyInvitationLink(invitationUrl)}
                    size="l"
                  />
                }
              >
                {t('invitations:action.copyInvitationLink')}
              </Tooltip>
            </IconButtonContainer>

            <Button
              icon="SendMessage"
              onClick={showSendInvitationModal}
              size="m"
            >
              {invitationSentCount >= 1
                ? t('invitations:action.resendInvitation')
                : t('invitations:action.sendInvitation')}
            </Button>
          </ButtonGroup>
        ) : null}
      </BarContainer>

      <Section>
        <Cell.Basic colSpan={[4, 6, 4]}>
          <SectionHeadline
            headline={t('invitations:title.personalInformation')}
            size="s"
          />
        </Cell.Basic>

        <Cell.Basic colSpan={[4, 6, 8]}>
          <Box>
            <Grid>
              {firstName ? (
                <Cell.Basic colSpan={[4, 6, 6]}>
                  <ReadOnlyField
                    text={firstName}
                    title={t('common:firstName')}
                  />
                </Cell.Basic>
              ) : null}

              {lastName ? (
                <Cell.Basic colSpan={[4, 6, 6]}>
                  <ReadOnlyField text={lastName} title={t('common:lastName')} />
                </Cell.Basic>
              ) : null}

              <Cell.Basic colSpan={[4, 6, 12]}>
                <ReadOnlyField
                  text={email}
                  title={t('common:glossary.email')}
                />
              </Cell.Basic>

              {location ? (
                <Cell.Basic colSpan={[4, 6, 6]}>
                  <ReadOnlyField
                    text={location}
                    title={t('common:label.location')}
                  />
                </Cell.Basic>
              ) : null}
            </Grid>
          </Box>
        </Cell.Basic>
      </Section>

      <Section>
        <Cell.Basic colSpan={[4, 6, 4]}>
          <SectionHeadline headline={t('common:label.language')} size="s" />

          <SectionText>{t('invitations:text.language')}</SectionText>
        </Cell.Basic>

        <Cell.Basic colSpan={[4, 6, 8]}>
          <Box>
            <Grid>
              <Cell.Basic colSpan={[4, 6, 6]}>
                <ReadOnlyField
                  text={displayedLanguage ?? NOT_AVAILABLE}
                  title={t('common:label.language')}
                />
              </Cell.Basic>
            </Grid>
          </Box>
        </Cell.Basic>
      </Section>

      <Section>
        <Cell.Basic colSpan={[4, 6, 4]}>
          <SectionHeadline
            headline={
              type === 'application'
                ? t('common:glossary.application')
                : t('common:glossary.tenant')
            }
            size="s"
          />

          <SectionText>
            {type === 'application'
              ? t('invitations:text.application')
              : t('invitations:text.tenant')}
          </SectionText>
        </Cell.Basic>

        <Cell.Basic colSpan={[4, 6, 8]}>
          <Box>
            {/* TODO: Reduce grid spacing throughout the whole app */}
            <Grid>
              {invitedToName ? (
                <Cell.Basic colSpan={[4, 6, 12]}>
                  <ReadOnlyField
                    text={invitedToName}
                    title={
                      type === 'application'
                        ? t('common:glossary.application')
                        : t('common:glossary.tenant')
                    }
                  />
                </Cell.Basic>
              ) : null}

              <Cell.Basic colSpan={[4, 6, 12]}>
                <ReadOnlyField
                  text={redirectUrl}
                  title={t('invitations:label.postInviteUrl')}
                />
              </Cell.Basic>
            </Grid>
          </Box>
        </Cell.Basic>
      </Section>

      {additionalData ? (
        <Section>
          <Cell.Basic colSpan={[4, 6, 4]}>
            <SectionHeadline
              headline={t('invitations:title.additionalData')}
              size="s"
            />

            <SectionText>
              {t('invitations:text.additionalData_application_read')}
            </SectionText>
          </Cell.Basic>

          <Cell.Basic colSpan={[4, 6, 8]}>
            <Box>
              <Grid>
                {Object.entries(additionalData).map(([title, text]) => (
                  <Cell.Basic colSpan={[4, 6, 6]} key={title}>
                    <ReadOnlyField text={text} title={title} />
                  </Cell.Basic>
                ))}
              </Grid>
            </Box>
          </Cell.Basic>
        </Section>
      ) : null}

      <SendInvitationModal
        data={{ code, email, sentCount: invitationSentCount }}
        isOpen={isSendInvitationModalOpen}
        onClose={closeSendInvitationModal}
      />
    </>
  );
};
